import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Container, Paper, Typography, Switch, FormControlLabel,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Chip, Divider, CircularProgress, Snackbar,
  IconButton, Accordion, AccordionSummary, AccordionDetails,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { format, addDays } from "date-fns";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4, 2),
    maxWidth: 860,
    margin: "0 auto",
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  notifCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  notifHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  previewBox: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    whiteSpace: "pre-wrap",
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: theme.spacing(1),
  },
  addTypeRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  companySelect: {
    marginBottom: theme.spacing(3),
    minWidth: 280,
  },
}));

const DEFAULT_NOTIF1_TEXT =
  "Hola {{nombre}}, te recordamos que mañana tenés una cita de *{{detalle}}* a las {{hora}} hs. — {{empresa}}";
const DEFAULT_NOTIF2_TEXT =
  "Hola {{nombre}}, hoy es el día de tu cita de *{{detalle}}* a las {{hora}} hs. ¡Te esperamos! — {{empresa}}";
const DEFAULT_NOTIF3_TEXT =
  "Hola {{nombre}}, en {{minutos}} minutos tenés tu cita de *{{detalle}}* ({{hora}} hs). — {{empresa}}";

const HOURS = Array.from({ length: 13 }, (_, i) => i);

const replacePreview = (template, { companyName, detailOptions, notif3Minutes }) => {
  const tomorrow = format(addDays(new Date(), 1), "dd/MM/yyyy");
  const firstDetail = detailOptions?.[0] || "Consulta";
  return template
    .replace(/\{\{nombre\}\}/g, "Juan García")
    .replace(/\{\{fecha\}\}/g, tomorrow)
    .replace(/\{\{hora\}\}/g, "10:00")
    .replace(/\{\{empresa\}\}/g, companyName || "WAIA")
    .replace(/\{\{detalle\}\}/g, firstDetail)
    .replace(/\{\{minutos\}\}/g, String(notif3Minutes || 60));
};

const AppointmentsSettings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user.isSuperAdmin;

  // Company selection (superadmin sees all companies)
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");

  // Wbots
  const [wbots, setWbots] = useState([]);

  // Settings state
  const [appointmentsEnabled, setAppointmentsEnabled] = useState(false);
  const [defaultWhatsappId, setDefaultWhatsappId] = useState("");
  const [notif1Enabled, setNotif1Enabled] = useState(true);
  const [notif2Enabled, setNotif2Enabled] = useState(true);
  const [notif2Hour, setNotif2Hour] = useState(8);
  const [notif3Enabled, setNotif3Enabled] = useState(true);
  const [notif3Minutes, setNotif3Minutes] = useState(60);
  const [notif1Text, setNotif1Text] = useState(DEFAULT_NOTIF1_TEXT);
  const [notif2Text, setNotif2Text] = useState(DEFAULT_NOTIF2_TEXT);
  const [notif3Text, setNotif3Text] = useState(DEFAULT_NOTIF3_TEXT);
  const [detailOptions, setDetailOptions] = useState([]);
  const [newDetailOption, setNewDetailOption] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackError, setSnackError] = useState(false);

  // Load companies (superadmin only)
  useEffect(() => {
    if (!isSuperAdmin) {
      setSelectedCompanyId(user.companyId);
      setCompanyName(user.company?.name || "");
      return;
    }
    api.get("/companies").then(({ data }) => {
      setCompanies(data?.companies || data || []);
      if (data?.companies?.length || data?.length) {
        const first = (data?.companies || data)[0];
        setSelectedCompanyId(first.id);
        setCompanyName(first.name);
      }
    }).catch(toastError);
  }, [isSuperAdmin, user.companyId, user.company?.name]);

  // Load settings for selected company
  const loadSettings = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const headers = isSuperAdmin ? { "x-company-id": selectedCompanyId } : {};
      const { data } = await api.get("/settings", { headers });
      const map = {};
      (data?.settings || data || []).forEach(s => { map[s.key] = s.value; });

      setAppointmentsEnabled(map["appointmentsEnabled"] === "true");
      setDefaultWhatsappId(map["appointmentsDefaultWhatsappId"] || "");
      setNotif1Enabled(map["appointmentsNotif1Enabled"] !== "false");
      setNotif2Enabled(map["appointmentsNotif2Enabled"] !== "false");
      setNotif2Hour(Number(map["appointmentsNotif2Hour"] ?? 8));
      setNotif3Enabled(map["appointmentsNotif3Enabled"] !== "false");
      setNotif3Minutes(Number(map["appointmentsNotif3MinutesBefore"] ?? 60));
      setNotif1Text(map["appointmentsNotif1Text"] || DEFAULT_NOTIF1_TEXT);
      setNotif2Text(map["appointmentsNotif2Text"] || DEFAULT_NOTIF2_TEXT);
      setNotif3Text(map["appointmentsNotif3Text"] || DEFAULT_NOTIF3_TEXT);

      const rawOpts = map["appointmentDetailOptions"];
      if (rawOpts) {
        try { setDetailOptions(JSON.parse(rawOpts)); } catch (_) { setDetailOptions([]); }
      } else {
        setDetailOptions([]);
      }

      // Load wbots for this company
      const { data: wbotData } = await api.get("/whatsapp", { headers });
      setWbots((wbotData || []).filter(w => w.status === "CONNECTED"));

      const c = companies.find(c => c.id === selectedCompanyId);
      if (c) setCompanyName(c.name);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, isSuperAdmin, companies]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const putSetting = async (key, value, headers = {}) => {
    await api.put(`/settings/${key}`, { value }, { headers });
  };

  const handleSave = async () => {
    setSaving(true);
    const headers = isSuperAdmin ? { "x-company-id": selectedCompanyId } : {};
    try {
      await Promise.all([
        putSetting("appointmentsEnabled", String(appointmentsEnabled), headers),
        putSetting("appointmentsDefaultWhatsappId", String(defaultWhatsappId), headers),
        putSetting("appointmentsNotif1Enabled", String(notif1Enabled), headers),
        putSetting("appointmentsNotif2Enabled", String(notif2Enabled), headers),
        putSetting("appointmentsNotif2Hour", String(notif2Hour), headers),
        putSetting("appointmentsNotif3Enabled", String(notif3Enabled), headers),
        putSetting("appointmentsNotif3MinutesBefore", String(notif3Minutes), headers),
        putSetting("appointmentsNotif1Text", notif1Text, headers),
        putSetting("appointmentsNotif2Text", notif2Text, headers),
        putSetting("appointmentsNotif3Text", notif3Text, headers),
        putSetting("appointmentDetailOptions", JSON.stringify(detailOptions), headers),
      ]);
      setSnackError(false);
      setSnackOpen(true);
    } catch (err) {
      toastError(err);
      setSnackError(true);
      setSnackOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const addDetailOption = () => {
    const trimmed = newDetailOption.trim();
    if (!trimmed || detailOptions.includes(trimmed)) return;
    setDetailOptions(prev => [...prev, trimmed]);
    setNewDetailOption("");
  };

  const removeDetailOption = (opt) => {
    setDetailOptions(prev => prev.filter(o => o !== opt));
  };

  const previewCtx = { companyName, detailOptions, notif3Minutes };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 80 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container className={classes.root}>
      <Typography variant="h5" style={{ marginBottom: 24, fontWeight: 700 }}>
        {i18n.t("appointmentsSettings.titulo")}
      </Typography>

      {/* Superadmin: company selector */}
      {isSuperAdmin && companies.length > 0 && (
        <FormControl variant="outlined" size="small" className={classes.companySelect}>
          <InputLabel>Empresa</InputLabel>
          <Select
            value={selectedCompanyId || ""}
            onChange={e => {
              setSelectedCompanyId(e.target.value);
              const c = companies.find(c => c.id === e.target.value);
              if (c) setCompanyName(c.name);
            }}
            label={i18n.t("appointmentsSettings.empresa")}
          >
            {companies.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* SECTION 1 — General */}
      <Paper className={classes.section} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>{i18n.t("appointmentsSettings.general")}</Typography>

        {isSuperAdmin && (
          <FormControlLabel
            control={
              <Switch
                checked={appointmentsEnabled}
                onChange={e => setAppointmentsEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("appointmentsSettings.activarModulo")}
          />
        )}
        {!isSuperAdmin && !appointmentsEnabled && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {i18n.t("appointmentsSettings.contactarAdmin")}
          </Typography>
        )}

        <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 16 }}>
          <InputLabel>Canal WhatsApp para avisos</InputLabel>
          <Select
            value={defaultWhatsappId}
            onChange={e => setDefaultWhatsappId(e.target.value)}
            label={i18n.t("appointmentsSettings.canalWhatsapp")}
          >
            <MenuItem value="">{i18n.t("appointmentsSettings.principalAuto")}</MenuItem>
            {wbots.map(w => (
              <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* SECTION 2 — Tipos de cita */}
      <Paper className={classes.section} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>{i18n.t("appointmentsSettings.tiposCita")}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {i18n.t("appointmentsSettings.tiposCitaDesc")}
        </Typography>

        {detailOptions.length === 0 && (
          <Button
            size="small"
            variant="outlined"
            style={{ marginBottom: 12 }}
            onClick={() => setDetailOptions(["Consulta", "Seguimiento", "Visita", "Reunión de presupuesto"])}
          >
            {i18n.t("appointmentsSettings.cargarEjemplos")}
          </Button>
        )}

        <div className={classes.chipRow}>
          {detailOptions.map(opt => (
            <Chip
              key={opt}
              label={opt}
              onDelete={() => removeDetailOption(opt)}
              size="small"
            />
          ))}
        </div>

        <div className={classes.addTypeRow}>
          <TextField
            size="small"
            variant="outlined"
            label={i18n.t("appointmentsSettings.nuevoTipo")}
            value={newDetailOption}
            onChange={e => setNewDetailOption(e.target.value)}
            onKeyPress={e => { if (e.key === "Enter") addDetailOption(); }}
            style={{ flex: 1 }}
          />
          <Button variant="contained" color="primary" onClick={addDetailOption} startIcon={<AddIcon />}>
            {i18n.t("appointmentsSettings.agregar")}
          </Button>
        </div>
      </Paper>

      {/* SECTION 3 — Avisos */}
      <Paper className={classes.section} elevation={2}>
        <Typography variant="h6" className={classes.sectionTitle}>{i18n.t("appointmentsSettings.avisosAutomaticos")}</Typography>

        {/* AVISO 1 */}
        <div className={classes.notifCard}>
          <div className={classes.notifHeader}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("appointmentsSettings.aviso1")}
            </Typography>
            <Switch
              checked={notif1Enabled}
              onChange={e => setNotif1Enabled(e.target.checked)}
              color="primary"
              size="small"
            />
          </div>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            label={i18n.t("appointmentsSettings.textoMensaje")}
            value={notif1Text}
            onChange={e => setNotif1Text(e.target.value)}
          />
          <Typography className={classes.helperText}>
            Variables: {"{{nombre}} {{fecha}} {{hora}} {{empresa}} {{detalle}}"}
          </Typography>
          <div className={classes.previewBox}>
            {replacePreview(notif1Text, previewCtx)}
          </div>
        </div>

        {/* AVISO 2 */}
        <div className={classes.notifCard}>
          <div className={classes.notifHeader}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("appointmentsSettings.aviso2")}
            </Typography>
            <Switch
              checked={notif2Enabled}
              onChange={e => setNotif2Enabled(e.target.checked)}
              color="primary"
              size="small"
            />
          </div>

          <FormControl variant="outlined" size="small" style={{ marginBottom: 12 }}>
            <InputLabel>Hora del aviso</InputLabel>
            <Select
              value={notif2Hour}
              onChange={e => setNotif2Hour(Number(e.target.value))}
              label={i18n.t("appointmentsSettings.horaAviso")}
            >
              {HOURS.map(h => (
                <MenuItem key={h} value={h}>{h}:00 hs</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            label={i18n.t("appointmentsSettings.textoMensaje")}
            value={notif2Text}
            onChange={e => setNotif2Text(e.target.value)}
          />
          <Typography className={classes.helperText}>
            Variables: {"{{nombre}} {{fecha}} {{hora}} {{empresa}} {{detalle}}"}
          </Typography>
          <div className={classes.previewBox}>
            {replacePreview(notif2Text, previewCtx)}
          </div>
          <Typography className={classes.helperText} style={{ marginTop: 8, fontStyle: "italic" }}>
            {i18n.t("appointmentsSettings.notaAviso2")}
          </Typography>
        </div>

        {/* AVISO 3 */}
        <div className={classes.notifCard}>
          <div className={classes.notifHeader}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("appointmentsSettings.aviso3")}
            </Typography>
            <Switch
              checked={notif3Enabled}
              onChange={e => setNotif3Enabled(e.target.checked)}
              color="primary"
              size="small"
            />
          </div>

          <TextField
            type="number"
            variant="outlined"
            size="small"
            label={i18n.t("appointmentsSettings.minutosAntes")}
            value={notif3Minutes}
            onChange={e => setNotif3Minutes(Number(e.target.value))}
            inputProps={{ min: 5, max: 1440 }}
            style={{ marginBottom: 12, width: 160 }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            label={i18n.t("appointmentsSettings.textoMensaje")}
            value={notif3Text}
            onChange={e => setNotif3Text(e.target.value)}
          />
          <Typography className={classes.helperText}>
            Variables: {"{{nombre}} {{fecha}} {{hora}} {{empresa}} {{detalle}} {{minutos}}"}
          </Typography>
          <div className={classes.previewBox}>
            {replacePreview(notif3Text, { ...previewCtx, notif3Minutes })}
          </div>
        </div>
      </Paper>

      <Button
        variant="contained"
        color="primary"
        size="large"
        className={classes.saveButton}
        onClick={handleSave}
        disabled={saving}
        fullWidth
      >
        {saving ? <CircularProgress size={22} color="inherit" /> : i18n.t("appointmentsSettings.guardarConfig")}
      </Button>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity={snackError ? "error" : "success"}>
          {snackError ? i18n.t("appointmentsSettings.errorGuardar") : i18n.t("appointmentsSettings.configGuardada")}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AppointmentsSettings;
